import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, X, Send, Sparkles, Mic, MicOff, Paperclip, Volume2, VolumeX, Trash2, Copy, Check } from "lucide-react";

const GEMINI_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent";
const GEM_KEYS = [
  "AIzaSyAEdfvBnlug6zO0qP0M4X5gDlqkuLapr4A",
  "AIzaSyCZkLcZN9DzAd_E5w4X2baq15YotJqyJ-o",
  "AIzaSyCdHkAJySADhrRXl3VnAtir5EtBYzWDq90",
  "AIzaSyCdAjRatkq7MgnBChxyjzdpldtmAe8DUR0",
];
const DS_KEY = "sk-c52c23d38917441f8ff087301c56cd39";
const DS_URL = "https://api.deepseek.com/chat/completions";

const SYS = `You are the Campus Energy Monitor assistant - a helpful guide for visitors to this website.
The project is a final-year IoT system at CPUT (Cape Peninsula University of Technology, Cape Town, South Africa) by HNK.
The system monitors 3-phase electrical energy using ESP32 + 3× PZEM-004T sensors, plus a DS18B20 temperature probe and MQ-135 gas sensor (CO₂/NH₃/smoke, GOOD/POOR/DANGER), TTGO T-Camera, PIR, buzzer and active cooling in a custom enclosure.
The web dashboard (React + Tailwind, deployed on Cloudflare Pages) features live gauges, air quality readout, LCD mirror, AI assistant, camera snapshots, remote control, and ThingSpeak/Grafana analytics.
It communicates over HiveMQ Cloud MQTT (WSS). The account is completely free. Contact: hhnk3693@gmail.com.
Keep all responses concise (2-3 sentences max). Be friendly and helpful. If asked something technical that goes beyond the project, politely redirect to the relevant page.`;

const SUGGESTIONS = [
  "How do I get started?",
  "What can this dashboard do?",
  "Tell me about the hardware",
  "Is it free?",
  "How do I contact Henock?",
];

function localReply(text, nav) {
  const q = text.toLowerCase();
  if (q.match(/start|begin|sign.?up|register/))
    return { text: "Click 'Get Started' to create a free account and land on the live dashboard.", action: { label: "Create account", to: "/register" } };
  if (q.match(/login|sign.?in/))
    return { text: "Use the Sign In button in the top-right corner.", action: { label: "Sign In", to: "/login" } };
  if (q.match(/feature|what.*(do|can)|capabilit/))
    return { text: "Live 3-phase metering, gauges, LCD mirror, AI assistant, camera, remote control, ThingSpeak analytics - all in one dashboard.", action: { label: "See Features", to: "/features" } };
  if (q.match(/hardware|esp32|sensor|pzem|mq.?135|gas|air.?quality/))
    return { text: "Runs on ESP32 + 3× PZEM-004T, DS18B20 temperature probe, MQ-135 gas sensor, TTGO T-Camera, PIR, buzzer and active cooling in a custom enclosure.", action: { label: "About", to: "/about" } };
  if (q.match(/free|price|cost/))
    return { text: "Completely free - it's a CPUT final-year engineering research project." };
  if (q.match(/contact|email|reach|henock/))
    return { text: "Reach HNK at hhnk3693@gmail.com.", action: { label: "Contact Page", to: "/contact" } };
  if (q.match(/hello|hi|hey/))
    return { text: "Hi! 👋 Ask me about getting started, the features, the hardware, or how to contact Henock." };
  return { text: "I'm here to help you explore the Campus Energy Monitor. Ask about features, the hardware, pricing, or getting started." };
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export function ChatWidget() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! 👋 I'm the Campus Energy guide. How can I help you get started?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  const [listening, setListening] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachUrl, setAttachUrl] = useState(null);
  const [copied, setCopied] = useState(null);

  const endRef = useRef(null);
  const fileRef = useRef(null);
  const keyIdxRef = useRef(0);
  const recRef = useRef(null);
  const chatHistRef = useRef([]);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // Keep chatHistRef in sync for Gemini conversation history
  useEffect(() => {
    chatHistRef.current = messages.flatMap((m) => {
      if (m.role === "bot") return [{ role: "model", parts: [{ text: m.text }] }];
      if (m.role === "user") return [{ role: "user", parts: [{ text: m.text }] }];
      return [];
    });
  }, [messages]);

  const speak = useCallback((text) => {
    if (!ttsOn || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 1.05;
    utt.pitch = 1;
    window.speechSynthesis.speak(utt);
  }, [ttsOn]);

  const callGemini = useCallback(async (userText, imgBase64, imgMime, attempt = 0) => {
    if (attempt >= GEM_KEYS.length) {
      // DeepSeek fallback
      const resp = await fetch(DS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${DS_KEY}` },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            { role: "system", content: SYS },
            ...chatHistRef.current.slice(-8).map((c) => ({
              role: c.role === "model" ? "assistant" : "user",
              content: c.parts[0].text,
            })),
            { role: "user", content: userText },
          ],
          max_tokens: 200,
        }),
      });
      if (!resp.ok) throw new Error("DS " + resp.status);
      const d = await resp.json();
      return d.choices?.[0]?.message?.content || "No response.";
    }

    const parts = [{ text: userText }];
    if (imgBase64) parts.unshift({ inline_data: { mime_type: imgMime, data: imgBase64 } });

    const r = await fetch(GEMINI_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-goog-api-key": GEM_KEYS[keyIdxRef.current] },
      body: JSON.stringify({
        system_instruction: { parts: [{ text: SYS }] },
        contents: [
          ...chatHistRef.current.slice(-8),
          { role: "user", parts },
        ],
        generationConfig: { temperature: 0.55, maxOutputTokens: 200 },
      }),
    });

    if (r.status === 429 || r.status === 403 || r.status === 503) {
      keyIdxRef.current = (keyIdxRef.current + 1) % GEM_KEYS.length;
      return callGemini(userText, imgBase64, imgMime, attempt + 1);
    }
    if (!r.ok) throw new Error("Gemini " + r.status);
    const d = await r.json();
    return d.candidates?.[0]?.content?.parts?.[0]?.text || "No response.";
  }, []);

  const send = useCallback(async (txt) => {
    const text = (txt ?? input).trim();
    if (!text && !attachment) return;
    const imgB64 = attachment ? await fileToBase64(attachment).catch(() => null) : null;
    const imgMime = attachment?.type || "image/jpeg";

    const userMsg = { role: "user", text: text || "(image)", ...(attachUrl ? { img: attachUrl } : {}) };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setAttachment(null);
    setAttachUrl(null);
    setLoading(true);

    try {
      const reply = await callGemini(text, imgB64, imgMime);
      setMessages((m) => [...m, { role: "bot", text: reply }]);
      speak(reply);
    } catch {
      const fallback = localReply(text, nav);
      setMessages((m) => [...m, { role: "bot", text: fallback.text, action: fallback.action }]);
      speak(fallback.text);
    } finally {
      setLoading(false);
    }
  }, [input, attachment, attachUrl, callGemini, speak, nav]);

  const toggleMic = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      recRef.current?.stop();
      recRef.current = null;
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput(t);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  }, [listening]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachment(f);
    setAttachUrl(URL.createObjectURL(f));
    e.target.value = "";
  };

  const copyMsg = (text, i) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(i);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const clearChat = () => {
    setMessages([{ role: "bot", text: "Hi! 👋 I'm the Campus Energy guide. How can I help you get started?" }]);
    setInput("");
    setAttachment(null);
    setAttachUrl(null);
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[var(--accent)] text-black shadow-xl flex items-center justify-center active:scale-95 transition-transform hover:brightness-110"
        aria-label="Open assistant"
      >
        {open ? <X size={24} /> : <Bot size={26} />}
        {!open && <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-[var(--danger)] border-2 border-[var(--bg)]" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-[390px] h-[560px] max-h-[80vh] flex flex-col rounded-2xl border border-[var(--bd)] bg-[var(--surface)] shadow-2xl overflow-hidden fade-up" data-testid="chat-widget-panel">

          {/* Header */}
          <div className="px-4 py-3 border-b border-[var(--bd)] bg-[var(--surface2)] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">Campus Energy Guide</div>
              <div className="text-[11px] text-[var(--t2)] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" /> AI · typically replies instantly</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setTtsOn((v) => !v)} title={ttsOn ? "Mute voice" : "Enable voice"} className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${ttsOn ? "text-[var(--accent)] bg-[var(--accent-soft)]" : "text-[var(--t2)] hover:text-[var(--t1)]"}`} data-testid="chat-tts-toggle">
                {ttsOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
              </button>
              <button onClick={clearChat} title="Clear chat" className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--t2)] hover:text-[var(--danger)] transition-colors" data-testid="chat-clear">
                <Trash2 size={15} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[84%] group relative">
                  <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--accent)] text-black rounded-br-sm" : "bg-[var(--surface2)] border border-[var(--bd)] rounded-bl-sm"}`}>
                    {m.img && <img src={m.img} alt="attachment" className="rounded-md mb-2 max-h-32 block" />}
                    {m.text}
                    {m.action && (
                      <button onClick={() => { nav(m.action.to); setOpen(false); }} className="mt-2 block w-full text-center px-3 py-1.5 rounded-md bg-[var(--accent)]/15 text-[var(--accent)] text-xs font-semibold hover:bg-[var(--accent)]/25 transition-colors" data-testid="chat-action-btn">
                        {m.action.label} →
                      </button>
                    )}
                  </div>
                  {m.role === "bot" && (
                    <button onClick={() => copyMsg(m.text, i)} className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-[var(--surface)] border border-[var(--bd)] flex items-center justify-center text-[var(--t2)] hover:text-[var(--t1)]" title="Copy" data-testid={`copy-msg-${i}`}>
                      {copied === i ? <Check size={10} /> : <Copy size={10} />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-[var(--surface2)] border border-[var(--bd)] px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
                  {[0, 1, 2].map((j) => (
                    <span key={j} className="w-1.5 h-1.5 rounded-full bg-[var(--t2)]" style={{ animation: `pulse-dot 1.2s ease-in-out ${j * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full text-xs border border-[var(--bd)] text-[var(--t2)] hover:border-[var(--accent)] hover:text-[var(--t1)] transition-colors" data-testid="chat-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Attachment preview */}
          {attachUrl && (
            <div className="px-3 pb-1 flex items-center gap-2">
              <img src={attachUrl} alt="preview" className="h-12 w-12 rounded-md object-cover border border-[var(--bd)]" />
              <span className="text-xs text-[var(--t2)] truncate flex-1">{attachment?.name}</span>
              <button onClick={() => { setAttachment(null); setAttachUrl(null); }} className="text-[var(--danger)] text-xs">✕</button>
            </div>
          )}

          {/* Input row */}
          <div className="p-2 sm:p-3 border-t border-[var(--bd)] flex items-center gap-1.5 sm:gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            <button onClick={() => fileRef.current?.click()} title="Attach image" className="hidden sm:flex w-8 h-8 sm:w-9 sm:h-9 rounded-md items-center justify-center text-[var(--t2)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] transition-colors shrink-0" data-testid="chat-attach">
              <Paperclip size={16} />
            </button>
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder={listening ? "Listening…" : "Ask me anything…"}
              className="flex-1 min-w-0 bg-[var(--surface2)] border border-[var(--bd)] rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors"
            />
            <button onClick={toggleMic} title={listening ? "Stop mic" : "Voice input"} className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors shrink-0 ${listening ? "bg-[var(--danger)] text-white" : "text-[var(--t2)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)]"}`} data-testid="chat-mic">
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <button onClick={() => send()} disabled={loading} data-testid="chat-widget-send" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[var(--accent)] text-black flex items-center justify-center shrink-0 active:scale-95 transition-transform disabled:opacity-50">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
